/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactElement, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Field,
    Input,
    makeStyles,
} from '@fluentui/react-components';
import { Column, Row } from '../common';
import { PartyControl } from '../controls/PartyControl';
import { AddressControl } from '../controls/AddressControl';
import { Party, useInitialValues } from '../../data/types';
import { PhonesPanel } from '../controls/PhonesPanel';
import { useValidation } from '../../data/types/validation';
import { useDialogControls } from './useDialogControls';

const useStyles = makeStyles({
    root: {},
    field: {
        width: '100%',
    },
});

export type AddPartyProps = {
    open?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    initialName?: string;
    onSubmit?: (values: any, editing?: boolean) => void;
    partyToEdit?: Party;
    children?: ReactElement<any>;
};
export const AddPartyDialog = (props: AddPartyProps) => {
    const { open, onOpenChange, initialName, onSubmit, children, partyToEdit } = props;

    //Hooks
    const initValues = useInitialValues();
    const { validateParty } = useValidation();

    //Styles
    const styles = useStyles();

    //Form
    const getInitialValues = (): Party => {
        if (!!partyToEdit) {
            return { ...initValues.party(), ...partyToEdit };
        } else {
            return { ...initValues.party(), name: initialName };
        }
    };
    const form = useFormik<Party>({
        initialValues: getInitialValues(),
        onSubmit: (values, { resetForm }) => {
            if (!!values.name) {
                !!onSubmit && onSubmit({ ...values }, !!partyToEdit);
            }
            resetForm({ values: !!partyToEdit ? partyToEdit : initValues.party() });
        },
        validationSchema: validateParty.schema,
    });

    //Dialog Controls
    const [dialogOpen, onOpenChangeHandler] = useDialogControls(open, onOpenChange);

    //UI Events
    const clickSubmit = (e: any) => {
        e.stopPropagation();
        form.submitForm();
    };
    const clickCancel = (e: any) => {
        e.stopPropagation();
        form.resetForm({ values: !!partyToEdit ? partyToEdit : initValues.party() });
    };

    //Effects
    useEffect(() => {
        if (!!dialogOpen && !!initialName && !form.values.name?.startsWith(initialName)) {
            form.resetForm({ values: { ...form.values, name: initialName || '' } });
        }
    }, [dialogOpen, initialName]);

    useEffect(() => {
        if (!!dialogOpen && !!partyToEdit) {
            form.resetForm({ values: !!partyToEdit ? partyToEdit : initValues.party() });
        }
    }, [dialogOpen, partyToEdit]);

    return (
        <Dialog
            open={dialogOpen}
            onOpenChange={onOpenChangeHandler}
        >
            <DialogTrigger>{children}</DialogTrigger>
            <DialogSurface aria-describedby={undefined} mountNode={undefined}>
                <DialogBody>
                    <DialogTitle>
                        {!!partyToEdit ? 'Edit' : 'Create New'} Client / 3rd Party
                    </DialogTitle>
                    <DialogContent>
                        <Column align='center'>
                            <PartyControl size='large' party={form.values} />
                            <Field
                                className={styles.field}
                                label={`Client / 3rd Party Name`}
                                required
                                size='medium'
                                validationMessage={form.errors.name}
                            >
                                <Input
                                    name='name'
                                    maxLength={validateParty.name.maxLength}
                                    value={form.values.name || ''}
                                    onChange={form.handleChange}
                                    placeholder={`Enter the name of the Client / 3rd Party`}
                                    data-testid='party-dialog-name-input'
                                />
                            </Field>
                            <AddressControl
                                address={form.values.address}
                                errors={form.errors.address}
                                onChange={form.handleChange}
                                testIdPrefix='party-dialog'
                            />
                            <Row>
                                <Column>
                                    <PhonesPanel
                                        phones={form.values.phone}
                                        onChangePhones={(phones) =>
                                            form.setFieldValue('phone', phones)
                                        }
                                    />
                                </Column>
                                <Column>
                                    <Field label='Url' size='medium'>
                                        <Input
                                            name='url'
                                            maxLength={validateParty.url.maxLength}
                                            value={form.values.url || ''}
                                            onChange={form.handleChange}
                                            placeholder='Enter Url'
                                            data-testid='party-dialog-url-input'
                                        />
                                    </Field>
                                    {/*<Field label="Logo" size='medium' >*/}
                                    {/*    <Input name='logoUrl' value={form.values.logoUrl} onChange={form.handleChange} />*/}
                                    {/*</Field>*/}
                                </Column>
                            </Row>
                        </Column>
                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            {!!partyToEdit ? (
                                <Button
                                    type='submit'
                                    appearance='primary'
                                    onClick={clickSubmit}
                                    disabled={!form.dirty || !form.isValid}
                                    data-testid='party-dialog-update-button'
                                >
                                    Update
                                </Button>
                            ) : (
                                <Button
                                    type='submit'
                                    appearance='primary'
                                    onClick={clickSubmit}
                                    disabled={!form.dirty || !form.isValid}
                                    data-testid='party-dialog-create-button'
                                >
                                    Create
                                </Button>
                            )}
                        </DialogTrigger>
                        <DialogTrigger disableButtonEnhancement>
                            <Button
                                appearance='secondary'
                                onClick={clickCancel}
                                data-testid='party-dialog-cancel-button'
                            >
                                Cancel
                            </Button>
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
